// Lib
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';

// Hooks
import useBodyFreeze from '@hooks/useBodyFreeze';
import { cn } from '@components/lib/utils';

// Entrance animation name → CSS keyframe (defined in src/index.css). The body
// reads it via the --bd-anim custom property so the .animate-out class can
// override it on dismiss.
const KEYFRAME: Record<string, string> = {
  FadeIn: 'mrbl-fade-in',
  FadeOut: 'mrbl-fade-out',
  RollUp: 'mrbl-roll-up',
  RollDown: 'mrbl-roll-down',
  RollLeft: 'mrbl-roll-left',
  ZoomIn: 'mrbl-zoom-in',
  FadeInFromNone: 'mrbl-fade-in-from-none',
};

interface BackdoorProps {
  onClick: () => void;
  children?: ReactNode;
  animation?: string;
  tone?: number;
  zIndex?: number;
  popUpRef?: React.RefObject<HTMLDivElement>;
  top?: number;
  id?: string;
  theme?: string;
  /** Classes for the modal body box (Popup supplies the layout). */
  bodyClassName?: string;
  /** Dynamic per-popup overrides (usePopup `style`). */
  bodyStyle?: React.CSSProperties;
  className?: string;
}

const isSafari = (globalThis as { safari?: unknown })?.safari !== undefined;

// Legacy popup callers pass styled-components-shaped style objects: nested
// selectors (`&&>div:first-child`), `@media (...)` keys, and `!important` on
// values. React's inline `style` supports none of these — it warns on the
// selector/media keys and silently ignores `!important` (which is why e.g.
// `background: 'white !important'` left modals transparent). Drop the
// selector/media keys and strip `!important` so the plain properties apply.
const plainStyle = (
  s?: React.CSSProperties,
): React.CSSProperties | undefined => {
  if (!s) return undefined;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(s)) {
    if (/[&@>:\s]/.test(key)) continue;
    const val = (s as Record<string, unknown>)[key];
    out[key] =
      typeof val === 'string' && val.includes('!important')
        ? val.replace(/\s*!important/gi, '').trim()
        : val;
  }
  return out as React.CSSProperties;
};

const SKIP_SWIPE_IDS = [
  'upload-avatar',
  'post-popup',
  'logInCard',
  'upgrade-modal',
  'link-change-menu',
  'open-rules',
];

const Backdoor: React.FC<BackdoorProps> = ({
  onClick,
  children,
  animation = 'FadeIn',
  tone = 0.4,
  zIndex = 201,
  popUpRef,
  top,
  id,
  theme,
  bodyClassName,
  bodyStyle,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const startPosY = useRef<number>(0);
  const contentHeight = useRef<number>(0);
  const startedSwipe = useRef<boolean>(false);
  useBodyFreeze(ref);

  const handleTouchStart = useCallback((event: TouchEvent) => {
    const touch = event.targetTouches[0];
    startPosY.current = touch.pageY;
    contentHeight.current = ref.current ? ref.current.offsetHeight : 0;
    startedSwipe.current = true;
  }, []);

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      const touch = event.changedTouches[0];

      if (
        ref.current &&
        startedSwipe &&
        touch.pageY > contentHeight.current / 2
      ) {
        startedSwipe.current = false;
        const position = 30 + touch.pageY - startPosY.current;

        if ((position * 100) / contentHeight.current > 30) {
          ref.current.style.transition = '';
          ref.current.style.bottom = `${contentHeight.current + 10}px)`;
          setTimeout(() => {
            onClick();
          }, 400);
        } else {
          ref.current.style.transition = '';
          ref.current.style.bottom = '';
        }
      }
    },
    [onClick],
  );

  const handleTouchMove = useCallback((event: TouchEvent) => {
    const touch = event.targetTouches[0];

    if (startedSwipe && touch.pageY < startPosY.current) {
      const position = 30 + touch.pageY - startPosY.current;
      if (position > 0 && ref.current) {
        ref.current.style.bottom = `${position}px`;
      }
    } else if (startedSwipe && touch.pageY > startPosY.current) {
      const position = 30 + touch.pageY - startPosY.current;
      if (position < contentHeight.current && ref.current) {
        ref.current.style.bottom = `-${position}px`;
      }
    }
  }, []);

  useEffect(() => {
    const body = ref.current;
    const enabled =
      document.body.offsetWidth <= 768 && body && !SKIP_SWIPE_IDS.includes(id);
    if (enabled) {
      body.addEventListener('touchstart', handleTouchStart);
      body.addEventListener('touchmove', handleTouchMove);
      body.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      if (enabled) {
        body.removeEventListener('touchstart', handleTouchStart);
        body.removeEventListener('touchmove', handleTouchMove);
        body.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, id]);

  return (
    <div
      ref={popUpRef}
      className="fixed inset-x-0 top-0 size-full max-[1024px]:top-[var(--bd-top)]"
      style={{ zIndex, ['--bd-top' as string]: `${top ?? 0}px` }}
    >
      <div
        onClick={onClick}
        className="backdoor-back absolute left-0 top-0 z-[5] size-full backdrop-blur-[5px]"
        style={{ background: `rgba(0, 0, 0, ${tone})` }}
      />
      <div
        ref={ref}
        id={id}
        className={cn(
          'backdoor-body card card-modal z-10',
          theme,
          bodyClassName,
          className,
        )}
        style={{
          ['--bd-anim' as string]: isSafari
            ? 'none'
            : KEYFRAME[animation] ?? 'mrbl-fade-in',
          ...plainStyle(bodyStyle),
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default Backdoor;
